import React, { useMemo } from 'react';
import { Measurement } from '../types';
import { getSwissTimeComponents } from '../utils/timezoneUtils';

interface TemperatureDayOverviewProps {
  measurements: Measurement[];
  title: string;
}



interface TimeSlotData {
  time: string;
  measured: number;
  perceived: string;
  measuredCount: number;
  perceivedCount: number;
}

const TemperatureDayOverview: React.FC<TemperatureDayOverviewProps> = ({ 
  measurements, 
  title
}) => {




  // Generate time slots from 6:00 to 22:00 in 30-minute intervals
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  }, []);



  // Calculate data for each time slot
  const timeSlotData = useMemo((): TimeSlotData[] => {
    const slotData: { [key: string]: { measured: number[], perceived: string[] } } = {};
    
    // Initialize time slots
    timeSlots.forEach(slot => {
      slotData[slot] = { measured: [], perceived: [] };
    });

    // Group measurements by time slot (using Swiss time)
    measurements.forEach(measurement => {
      const swissTimeComponents = getSwissTimeComponents(measurement.created_at);
      
      // Skip invalid dates
      if (isNaN(swissTimeComponents.hours) || isNaN(swissTimeComponents.minutes)) {
        console.warn('Invalid date found in TemperatureDayOverview:', measurement.created_at);
        return;
      }
      
      const hour = swissTimeComponents.hours;
      const minute = swissTimeComponents.minutes;
      const slot = `${hour.toString().padStart(2, '0')}:${(minute < 30 ? 0 : 30).toString().padStart(2, '0')}`;
      
      if (slotData[slot]) {
        // Add measured temperature
        if (measurement.measured_temperature > 0) {
          slotData[slot].measured.push(measurement.measured_temperature);
        }
        
        // Add perceived comfort level
        if (measurement.perceived_temperature) {
          slotData[slot].perceived.push(measurement.perceived_temperature);
        }
      }
    });

    // Calculate averages for each time slot
    return timeSlots.map(slot => {
      const measuredTemps = slotData[slot].measured;
      const perceivedComforts = slotData[slot].perceived;

      const measuredAvg = measuredTemps.length > 0 
        ? measuredTemps.reduce((a, b) => a + b, 0) / measuredTemps.length 
        : 0;
      
      // Get most common perceived comfort level
      const comfortCounts: { [key: string]: number } = {};
      perceivedComforts.forEach(comfort => {
        comfortCounts[comfort] = (comfortCounts[comfort] || 0) + 1;
      });
      const mostCommonComfort = Object.keys(comfortCounts).length > 0
        ? Object.entries(comfortCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0]
        : '';

      return {
        time: slot,
        measured: Math.round(measuredAvg * 10) / 10,
        perceived: mostCommonComfort,
        measuredCount: measuredTemps.length,
        perceivedCount: perceivedComforts.length,
      };
    });
  }, [measurements, timeSlots]);

  const getTemperatureColor = (temp: number) => {
    if (temp < 6) return '#284496';      // Sehr kalt
    if (temp < 11) return '#5d6ab0';     // Kalt
    if (temp < 15) return '#9498cc';     // Kühl
    if (temp < 19) return '#cacbe7';     // Leicht kühl
    if (temp < 22) return '#ffffff';     // Behaglich
    if (temp < 26) return '#f0cdbe';     // Leicht warm
    if (temp < 32) return '#e09a80';     // Warm
    if (temp < 37) return '#cf674a';     // Heiss
    return '#be331e';                    // Sehr heiss
  };

  const getPerceivedColor = (perceived: string) => {
    switch (perceived) {
      case 'Sehr kalt': return '#284496';
      case 'Kalt': return '#5d6ab0';
      case 'Kühl': return '#9498cc';
      case 'Leicht kühl': return '#cacbe7';
      case 'Behaglich': return '#ffffff';
      case 'Leicht warm': return '#f0cdbe';
      case 'Warm': return '#e09a80';
      case 'Heiss': return '#cf674a';
      case 'Sehr heiss': return '#be331e';
      default: return '#f3f4f6';
    }
  };



  return (
    <div className="bg-white rounded-lg p-6 shadow-md">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      


      {/* Temperature Timeline */}
      <div className="space-y-4">
        {/* Perceived Temperature Row */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Gefühlt</h4>
          </div>
          <div className="flex space-x-1 md:space-x-2">
            {timeSlotData.map((slot) => (
              <div
                key={`perceived-${slot.time}`}
                className="flex-1 h-8 rounded border border-gray-200"
                style={{ 
                  backgroundColor: slot.perceived ? getPerceivedColor(slot.perceived) : '#f3f4f6',
                }}
                title={`${slot.time}: ${slot.perceived || 'Keine Daten'} (${slot.perceivedCount} Bewertungen)`}
              >
              </div>
            ))}
          </div>
        </div>

        {/* Measured Temperature Row */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Gemessen</h4>
          </div>
          <div className="flex space-x-1 md:space-x-2">
            {timeSlotData.map((slot) => (
              <div
                key={`measured-${slot.time}`}
                className="flex-1 h-8 rounded border border-gray-200"
                style={{ 
                  backgroundColor: slot.measured > 0 ? getTemperatureColor(slot.measured) : '#f3f4f6',
                }}
                title={`${slot.time}: ${slot.measured > 0 ? slot.measured.toFixed(1) + '°C' : 'Keine Daten'} (${slot.measuredCount} Messungen)`}
              >
              </div>
            ))}
          </div>
        </div>

        {/* Time Labels */}
        <div className="flex justify-between text-xs text-gray-500 px-1">
          <span>6:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>22:00</span>
        </div>
      </div>

      {/* Temperature Color Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Temperatur-Skala</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#284496' }}></div>
            <span>Sehr kalt (0-6°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#5d6ab0' }}></div>
            <span>Kalt (6-11°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#9498cc' }}></div>
            <span>Kühl (11-15°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#cacbe7' }}></div>
            <span>Leicht kühl (15-19°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: '#ffffff' }}></div>
            <span>Behaglich (19-22°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f0cdbe' }}></div>
            <span>Leicht warm (22-26°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#e09a80' }}></div>
            <span>Warm (26-32°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#cf674a' }}></div>
            <span>Heiss (32-37°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#be331e' }}></div>
            <span>Sehr heiss (&gt;37°C)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f3f4f6' }}></div>
            <span>Keine Daten</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemperatureDayOverview;
